import React, { useState } from "react";
import { Alert, DialogActions } from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import DialogLayout from "../../../Components/DialogLayout";
import ButtonField from "../../../Components/Ui/ButtonField";
import { useTailwindBrand } from "../../../Components/shared";
import { ENV_DECLARONS, getActiveClientBrandConfig, getActivePrestationBrandConfig, isBrandActive } from "../../../config/environment.config";
import { TravelInfo } from "../../../Repositories/TravelRepository";

const FILE_TYPES = ["PDF", "PNG", "JPEG", "JPG"];

interface UploadPreuveModalProps {
    open: boolean;
    travelInfo: TravelInfo;
    onConfirm: (file: File) => void;
    onClose: () => void;
    /**
     * P6 : bascule vers le dépôt d'un justificatif d'abonnement de transports
     * en commun. Proposé seulement quand le mode de déplacement s'y prête
     * (transports en commun, train) ou n'est pas connu.
     */
    onUseSubscription?: () => void;
    /** Mode « Transports en commun » ou « Train » : la preuve ponctuelle est un billet. */
    ticketMode?: boolean;
}

/**
 * Preuve de déplacement obligatoire (scénario > 160 km) : titre de transport,
 * facture, etc. Le cours sera enregistré mais pas déclaré tant que l'admin
 * n'a pas approuvé la demande.
 */
const UploadPreuveModal = ({ open, travelInfo, onConfirm, onClose, onUseSubscription, ticketMode = false }: UploadPreuveModalProps) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState("");

    const distance = travelInfo.distanceKm != null ? travelInfo.distanceKm.toFixed(1) : "—";

    const handleConfirm = () => {
        if (!file) {
            setError("La preuve de déplacement est obligatoire pour continuer.");
            return;
        }
        setError("");
        onConfirm(file);
    };

    return (
        <DialogLayout
            title="Preuve de déplacement requise 🧾"
            open={open}
            handleClose={onClose}
            handleCloseIcon={onClose}
            customDialogActions="hidden"
            maxWidth="md"
            text={
                <div className={`${common.body} space-y-4`}>
                    <p>
                        La distance entre votre adresse et celle de votre {getActiveClientBrandConfig()} est
                        de <span className={`font-semibold ${classes.primaryText}`}>{distance} km</span>, soit
                        plus de {travelInfo.thresholdHigh} km. Une preuve de déplacement est{" "}
                        <span className="font-semibold">obligatoire</span> :{" "}
                        {ticketMode
                            ? "votre abonnement, ou à défaut le billet de ce déplacement."
                            : "titre de transport, facture de péage ou de carburant, réservation, etc."}
                    </p>

                    <FileUploader
                        handleChange={(f: File) => { setFile(f); setError(""); }}
                        name="preuve"
                        types={FILE_TYPES}
                        label={ticketMode ? "Déposez ou cliquez pour ajouter votre billet" : "Déposez ou cliquez pour ajouter votre preuve de déplacement"}
                    />
                    {file && (
                        <p className="text-sm">
                            Fichier sélectionné : <span className="font-semibold">{file.name}</span>
                        </p>
                    )}

                    <Alert severity="info" variant="outlined" sx={{ fontFamily: brandConfig.fontFamily }}>
                        Votre {getActivePrestationBrandConfig()} sera enregistré{isBrandActive(ENV_DECLARONS) ? "e" : ""}{" "}
                        <span className="font-semibold">en attente de validation admin</span>. {isBrandActive(ENV_DECLARONS) ? "Elle" : "Il"} ne sera
                        déclaré{isBrandActive(ENV_DECLARONS) ? "e" : ""} qu'après approbation de votre preuve de déplacement. En cas de rejet,
                        vous serez notifié par e-mail.
                    </Alert>

                    {/* P6 : un abonnement couvre toutes les déclarations de sa période */}
                    {onUseSubscription && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm">
                                {ticketMode
                                    ? "Déposez votre abonnement (Navigo, TER...) une seule fois : il servira de preuve pour toutes vos déclarations tant qu'il est valide, sans billet à joindre."
                                    : "Vous voyagez en transports en commun ? Déposez votre abonnement (Navigo, TER...) une seule fois : il servira de preuve pour toutes vos déclarations tant qu'il est valide."}
                            </p>
                            <ButtonField
                                variant="outlined"
                                onClick={onUseSubscription}
                                label="J'ai un abonnement 🚌"
                            />
                        </div>
                    )}
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                </div>
            }
            btnChildren={
                <DialogActions sx={{ py: 2.5, px: 3, borderTop: "1px solid", borderColor: "divider" }} className={common.body}>
                    <ButtonField variant="outlined" onClick={onClose} label="Annuler" />
                    <ButtonField variant="contained" onClick={handleConfirm} label={ticketMode ? "Joindre le billet et continuer" : "Joindre et continuer"} />
                </DialogActions>
            }
        />
    );
};

export default UploadPreuveModal;
